export const QUESTION_TYPE = {
  SINGLE: 1,
  MULTI: 2,
  FILL: 3,
  JUDGE: 4,
  SHORT: 5,
  MATCHING: 6,
  SHARED_OPTION: 7,
  ASSESSMENT: 8,
  CLOCK: 9,
  WORD_SELECT: 10,
  OPTIONAL: 11
}

const isObject = value => value !== null && typeof value === 'object' && !Array.isArray(value)

const TYPE_TEXT = {
  1: '单选题',
  2: '多选题',
  3: '填空题',
  4: '判断题',
  5: '简答题',
  6: '连线题',
  7: '共用选项题',
  8: '测评题',
  9: '钟表题',
  10: '选词填空题',
  11: '选做题'
}

export function escapeQuestionHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

export function renderQuestionContent(value) {
  if (value === null || typeof value === 'undefined') return '-'
  const raw = String(value).trim()
  if (!raw) return '-'
  if (/<[^>]+>/.test(raw)) return raw
  return escapeQuestionHtml(raw).replace(/\n/g, '<br/>')
}

export function parseQuestionPayload(question = {}) {
  const payload = question.payload
  if (isObject(payload)) return payload
  if (typeof payload !== 'string' || !payload.trim()) return {}

  try {
    const parsed = JSON.parse(payload)
    return isObject(parsed) ? parsed : {}
  } catch (error) {
    return {}
  }
}

export function getQuestionType(question = {}) {
  const payload = parseQuestionPayload(question)
  const type = Number(question.question_type || payload.question_type || 0)
  if (type) return type

  const typeKey = question.type || payload.type || ''
  const byKey = {
    QuestionChoice: QUESTION_TYPE.SINGLE,
    MultiChoice: QUESTION_TYPE.MULTI,
    QuestionGapFilling: QUESTION_TYPE.FILL,
    QuestionYesNo: QUESTION_TYPE.JUDGE,
    QuestionCommon: QUESTION_TYPE.SHORT,
    QuestionMatching: QUESTION_TYPE.MATCHING,
    QuestionSameChoose: QUESTION_TYPE.SHARED_OPTION,
    QuestionAssessment: QUESTION_TYPE.ASSESSMENT,
    QuestionClock: QUESTION_TYPE.CLOCK,
    QuestionChooseWordGapFilling: QUESTION_TYPE.WORD_SELECT,
    QuestionOptional: QUESTION_TYPE.OPTIONAL
  }
  return byKey[typeKey] || 0
}

export function getQuestionTypeWord(question = {}) {
  return question.question_type_word || question.typeAlias || TYPE_TEXT[getQuestionType(question)] || ''
}

export function getQuestionTitle(question = {}) {
  const payload = parseQuestionPayload(question)
  return question.question || question.question_title || question.title || payload.content || payload.title || ''
}

export function normalizeQuestionOption(option, index = 0) {
  const fallbackKey = String.fromCharCode(65 + index)
  if (!isObject(option)) {
    return { key: fallbackKey, content: String(option || '') }
  }

  return {
    ...option,
    key: String(option.key ?? option.name ?? option.label ?? option.id ?? fallbackKey),
    content: String(option.content ?? option.value ?? option.text ?? option.value_text ?? option.title ?? option.name ?? '')
  }
}

function firstArray(...values) {
  return values.find(value => Array.isArray(value) && value.length) || []
}

export function questionOptions(question = {}, parentQuestion = null) {
  const payload = parseQuestionPayload(question)
  const parentPayload = parentQuestion ? parseQuestionPayload(parentQuestion) : {}
  const rawOptions = firstArray(
    question.options,
    question.option_list,
    getQuestionType(question) === QUESTION_TYPE.SHARED_OPTION && isObject(question.options)
      ? question.options.options
      : null,
    payload.options,
    parentQuestion && parentQuestion.shared_options,
    parentQuestion && parentQuestion.options && parentQuestion.options.options,
    parentPayload.options
  )

  if (getQuestionType(question) === QUESTION_TYPE.JUDGE && rawOptions.length === 0) {
    return [
      { key: '1', content: '正确' },
      { key: '0', content: '错误' }
    ]
  }

  return rawOptions.map((option, index) => normalizeQuestionOption(option, index))
}

export function matchingLeftOptions(question = {}) {
  const payload = parseQuestionPayload(question)
  const options = isObject(question.options) ? question.options : {}
  const list = firstArray(payload.leftOptions, options.leftOptions, options.left, options.questions, options.options, options.items)
  return list.map((item, index) => normalizeQuestionOption({ ...item, key: item.name || item.key }, index))
}

export function matchingRightOptions(question = {}) {
  const payload = parseQuestionPayload(question)
  const options = isObject(question.options) ? question.options : {}
  const list = firstArray(payload.rightOptions, options.rightOptions, options.right, options.answers, options.targets, options.matches)
  return list.map((item, index) => normalizeQuestionOption({ ...item, key: item.name || item.key }, index))
}

export function sharedOptionQuestions(question = {}) {
  const payload = parseQuestionPayload(question)
  const options = isObject(question.options) ? question.options : {}
  const answerList = Array.isArray(payload.answerList) ? payload.answerList : []
  const list = firstArray(payload.questions, options.questions, answerList[2])

  return list.map((item, index) => {
    const source = isObject(item) ? item : { content: item }
    return {
      ...source,
      name: source.name || index + 1,
      question_id: source.question_id || source.id || source.name || index + 1,
      question: source.question || source.content || '',
      question_type: Number(source.question_type || source.typeId || (source.type === 'MultiChoice' ? 2 : 1)) || 1,
      question_type_word: source.question_type_word || source.typeAlias || ''
    }
  })
}

export function assessmentOptions(question = {}) {
  const payload = parseQuestionPayload(question)
  const options = isObject(question.options) ? question.options : {}
  const list = firstArray(payload.subQuestions, options.subQuestions)
  return list.map((item, index) => normalizeQuestionOption(item, index))
}

export function assessmentMaxSelections(question = {}) {
  const payload = parseQuestionPayload(question)
  const options = isObject(question.options) ? question.options : {}
  const configured = Number(payload.selectMaxNum || options.selectMaxNum || 0)
  return configured > 0 ? configured : assessmentOptions(question).length
}

export function clockConfig(question = {}) {
  const config = Array.isArray(question.clock_config) ? question.clock_config : []
  const groups = config.map((group, index) => ({
    type: String(group && group.type !== undefined ? group.type : index),
    time_count: Math.max(1, Number(group && group.time_count) || 1)
  }))

  return groups.length ? groups : [{ type: '0', time_count: 1 }]
}

export function wordSelectOptions(question = {}) {
  const payload = parseQuestionPayload(question)
  const answerList = Array.isArray(payload.answerList) ? payload.answerList : []
  const options = isObject(question.options) ? question.options : {}
  const list = firstArray(answerList[0], question.options, options.words, options.options, options.candidates)
  return list.map((item, index) => normalizeQuestionOption(item, index))
}

export function optionalQuestions(question = {}) {
  const payload = parseQuestionPayload(question)
  const options = isObject(question.options) ? question.options : {}
  const list = firstArray(payload.subQuestions, options.subQuestions)

  return list.map((item, index) => {
    const source = isObject(item) ? item : {}
    const data = isObject(source.data) ? source.data : {}
    const questionType = source.answer_mode === 'multiple' || /多选/.test(String(source.name || ''))
      ? QUESTION_TYPE.MULTI
      : QUESTION_TYPE.SINGLE
    const rawOptions = firstArray(data.answer, data.options)

    return {
      ...source,
      answer_name: index + 1,
      question: data.content || source.content || '',
      question_type: questionType,
      question_type_word: source.typeAlias || source.name || TYPE_TEXT[questionType] || '',
      options: rawOptions.map((option, optionIndex) => normalizeQuestionOption(option, optionIndex))
    }
  })
}

export function optionalRequiredCount(question = {}) {
  const payload = parseQuestionPayload(question)
  const options = isObject(question.options) ? question.options : {}
  const available = optionalQuestions(question).length
  const configured = Number(payload.optionalNum || options.optionalNum || 1)
  return Math.min(available, Math.max(1, configured))
}

export function blankCount(question = {}) {
  const payload = parseQuestionPayload(question)
  const answerList = Array.isArray(payload.answerList) ? payload.answerList : []
  const options = question.options

  if (Number(question.gap_count) > 0) return Number(question.gap_count)
  if (Number(payload.gapCount) > 0) return Number(payload.gapCount)
  if (Array.isArray(answerList[1]) && answerList[1].length) return answerList[1].length
  if (Array.isArray(options) && options.length && getQuestionType(question) !== QUESTION_TYPE.WORD_SELECT) return options.length
  if (options && Array.isArray(options.answers) && options.answers.length) return options.answers.length
  if (options && Array.isArray(options.blanks) && options.blanks.length) return options.blanks.length

  const matches = String(getQuestionTitle(question)).match(/<u\b[^>]*>.*?<\/u>|_{3,}|（\s*）|\(\s*\)/gi)
  return matches && matches.length ? matches.length : 1
}

function collectionHtml(items, nameSelector, contentSelector) {
  if (!Array.isArray(items) || !items.length) return ''
  return items.map((item, index) => {
    const source = isObject(item) ? item : { content: item }
    return `<div><strong>${escapeQuestionHtml(nameSelector(source, index))}：</strong>${renderQuestionContent(contentSelector(source, index))}</div>`
  }).join('')
}

export function buildQuestionDisplaySections(question = {}, options = {}) {
  const { showAnswers = false, hideInteractiveBlocks = false } = options
  const payload = parseQuestionPayload(question)
  const type = getQuestionType(question)
  const sections = []

  if (type === QUESTION_TYPE.MATCHING) {
    const leftHtml = collectionHtml(matchingLeftOptions(question), item => item.key, item => item.content)
    const rightHtml = collectionHtml(matchingRightOptions(question), item => item.key, item => item.content)
    if (leftHtml) sections.push({ title: '左侧连线项', html: leftHtml })
    if (rightHtml) sections.push({ title: '右侧连线项', html: rightHtml })
  }

  if (type === QUESTION_TYPE.SHARED_OPTION) {
    const commonHtml = collectionHtml(questionOptions(question), item => item.key, item => item.content)
    if (commonHtml) sections.push({ title: '共用选项', html: commonHtml })
    if (!hideInteractiveBlocks) {
      const answerList = Array.isArray(payload.answerList) ? payload.answerList : []
      const answers = Array.isArray(answerList[3]) ? answerList[3] : []
      const questionHtml = collectionHtml(
        sharedOptionQuestions(question),
        (item, index) => item.name ? `第 ${item.name} 题` : `第 ${index + 1} 题`,
        (item, index) => {
          const answer = Array.isArray(item.answer) ? item.answer.join('、') : (item.answer || (answers[index] && answers[index].content) || '-')
          return [
            item.question_type_word ? `题型：${item.question_type_word}` : '',
            item.question || item.content || '',
            showAnswers ? `答案：${answer}` : ''
          ].filter(Boolean).join('\n')
        }
      )
      if (questionHtml) sections.push({ title: '子题', html: questionHtml })
    }
  }

  if (type === QUESTION_TYPE.ASSESSMENT) {
    const legacyOptions = isObject(question.options) ? question.options : {}
    const items = firstArray(payload.subQuestions, legacyOptions.subQuestions)
    const html = collectionHtml(
      items,
      (item, index) => item.name || index + 1,
      item => showAnswers ? `${item.content || ''}\n分值：${Number(item.score || 0)}` : item.content || ''
    )
    if (html) sections.push({ title: '测评选项', html })
  }

  if (type === QUESTION_TYPE.CLOCK && showAnswers) {
    const groups = firstArray(payload.answer, question.correct_answer)
    const html = groups.map((group, groupIndex) => {
      const values = Array.isArray(group.data) ? group.data : []
      const times = values.map((item) => {
        const hour = String(item.hour ?? '00').padStart(2, '0')
        const minute = String(item.minute ?? '00').padStart(2, '0')
        return `${item.period || ''} ${hour}:${minute}`.trim()
      })
      return `<div><strong>${escapeQuestionHtml(group.type || `第 ${groupIndex + 1} 组`)}：</strong>${escapeQuestionHtml(times.join('、') || '-')}</div>`
    }).join('')
    if (html) sections.push({ title: '钟表答案', html })
  }

  if (type === QUESTION_TYPE.WORD_SELECT) {
    const wordsHtml = collectionHtml(wordSelectOptions(question), item => item.key, item => item.content)
    if (wordsHtml) sections.push({ title: '候选词', html: wordsHtml })
    if (showAnswers) {
      const answerList = Array.isArray(payload.answerList) ? payload.answerList : []
      const answers = firstArray(answerList[1], question.correct_answer)
      const answerHtml = collectionHtml(answers, (item, index) => item.name || `空${index + 1}`, item => item.content)
      if (answerHtml) sections.push({ title: '填空答案', html: answerHtml })
    }
  }

  if (type === QUESTION_TYPE.OPTIONAL) {
    const legacyOptions = isObject(question.options) ? question.options : {}
    const items = firstArray(payload.subQuestions, legacyOptions.subQuestions)
    const optionalNum = Number(payload.optionalNum || legacyOptions.optionalNum || 0)
    const settingParts = [
      optionalNum ? `任选 ${optionalNum} 题` : '',
      showAnswers && payload.score !== undefined ? `总分 ${payload.score}` : ''
    ].filter(Boolean)
    if (settingParts.length) {
      sections.push({ title: '选做要求', html: `<div>${escapeQuestionHtml(settingParts.join('，'))}</div>` })
    }
    const html = collectionHtml(
      items,
      (item, index) => `第 ${index + 1} 小题${item.name ? `（${item.name}）` : ''}`,
      (item) => {
        const data = isObject(item.data) ? item.data : {}
        const optionLines = Array.isArray(data.answer)
          ? data.answer.map((option, optionIndex) => {
            const marker = showAnswers && option.isanswer ? '（正确）' : ''
            return `${option.name || optionIndex + 1}. ${option.content || ''}${marker}`
          }).join('\n')
          : ''
        return [data.content || item.content || '', optionLines].filter(Boolean).join('\n')
      }
    )
    if (html) sections.push({ title: '选做子题', html })
  }

  return sections
}

const MAX_ANSWER_DEPTH = 8
const MAX_JSON_DECODE_DEPTH = 4

function unwrapAnswerJson(value, remaining = MAX_JSON_DECODE_DEPTH) {
  if (typeof value !== 'string' || !value.trim() || remaining <= 0) return value

  try {
    const parsed = JSON.parse(value)
    return parsed === value ? value : unwrapAnswerJson(parsed, remaining - 1)
  } catch (error) {
    return value
  }
}

function hasAnswerValue(value) {
  if (value === null || typeof value === 'undefined') return false
  return typeof value !== 'string' || value.trim() !== ''
}

function hasDeepAnswerValue(value, seen = []) {
  const raw = unwrapAnswerJson(value)
  if (!hasAnswerValue(raw)) return false
  if (typeof raw !== 'object') return true
  if (seen.includes(raw)) return false

  const nextSeen = seen.concat([raw])
  if (Array.isArray(raw)) return raw.some(item => hasDeepAnswerValue(item, nextSeen))
  return Object.values(raw).some(item => hasDeepAnswerValue(item, nextSeen))
}

function answerField(source, keys) {
  if (!isObject(source)) return undefined
  return keys.reduce((result, key) => result === undefined && hasDeepAnswerValue(source[key]) ? source[key] : result, undefined)
}

function namedAnswerValues(value) {
  const raw = unwrapAnswerJson(value)
  if (Array.isArray(raw)) return raw
  if (isObject(raw)) {
    const hasNamedFields = ['name', 'key', 'id', 'question_id', 'content', 'value', 'selected', 'answer']
      .some(field => Object.prototype.hasOwnProperty.call(raw, field))
    if (hasNamedFields) return [raw]
    return Object.keys(raw).map(name => ({ name, content: raw[name] }))
  }
  return raw === null || typeof raw === 'undefined' ? [] : [raw]
}

function indexedAnswerValue(item) {
  if (!isObject(item)) return item
  return ['content', 'value', 'selected', 'answer']
    .reduce((result, key) => result === undefined && Object.prototype.hasOwnProperty.call(item, key) ? item[key] : result, undefined)
}

function hasIndexedAnswer(value) {
  return namedAnswerValues(value).some(item => hasDeepAnswerValue(indexedAnswerValue(item)))
}

function assessmentAnswerValue(item) {
  if (!isObject(item)) return item
  return ['selected', 'answer', 'value', 'name', 'content']
    .reduce((result, key) => result === undefined && Object.prototype.hasOwnProperty.call(item, key) ? item[key] : result, undefined)
}

function hasAssessmentAnswer(value) {
  const raw = unwrapAnswerJson(value)
  const fields = ['selected', 'answer', 'value', 'name', 'content']
  const items = Array.isArray(raw)
    ? raw
    : (isObject(raw) && !fields.some(field => Object.prototype.hasOwnProperty.call(raw, field))
        ? Object.values(raw)
        : [raw])
  return items.some(item => hasDeepAnswerValue(assessmentAnswerValue(item)))
}

function hasMatchingAnswer(value) {
  const rows = Array.isArray(value) ? value : [value]
  return rows.some(row => {
    if (!isObject(row)) return hasDeepAnswerValue(row)
    return hasDeepAnswerValue(['right', 'content', 'target', 'to']
      .reduce((result, key) => result === undefined && Object.prototype.hasOwnProperty.call(row, key) ? row[key] : result, undefined))
  })
}

function hasClockAnswer(value) {
  const groups = Array.isArray(value) ? value : [value]
  return groups.some(group => {
    if (!isObject(group)) return hasDeepAnswerValue(group)
    const data = unwrapAnswerJson(group.data)
    const times = Array.isArray(data) ? data : [group]
    return times.some(time => isObject(time) && ['period', 'hour', 'minute'].some(field => hasDeepAnswerValue(time[field])))
  })
}

export function isQuestionAnswerEmpty(answer, question = {}) {
  const value = unwrapAnswerJson(answer)
  const type = Number(question && (question.question_type || question.questionType))

  if (type === QUESTION_TYPE.MATCHING) return !hasMatchingAnswer(value)
  if (type === QUESTION_TYPE.ASSESSMENT) return !hasAssessmentAnswer(value)
  if ([QUESTION_TYPE.SHARED_OPTION, QUESTION_TYPE.WORD_SELECT, QUESTION_TYPE.OPTIONAL].includes(type)) {
    return !hasIndexedAnswer(value)
  }
  if (type === QUESTION_TYPE.CLOCK) return !hasClockAnswer(value)
  return !hasDeepAnswerValue(value)
}

function answerOptions(question) {
  const source = unwrapAnswerJson(question && question.options)
  if (Array.isArray(source)) return source.map(item => unwrapAnswerJson(item))
  if (isObject(source) && Array.isArray(source.options)) return source.options.map(item => unwrapAnswerJson(item))
  if (question && Array.isArray(question.shared_options)) return question.shared_options.map(item => unwrapAnswerJson(item))
  return []
}

function answerOption(option, index) {
  if (!isObject(option)) {
    return { key: String(index + 1), content: String(option ?? '') }
  }
  const rawContent = String(answerField(option, ['content', 'value', 'text', 'title']) ?? '')
  return {
    key: String(answerField(option, ['key', 'name', 'label', 'id']) ?? index + 1),
    content: rawContent.replace(/<[^>]+>/g, ' ').replace(/&nbsp;/gi, ' ').replace(/\s+/g, ' ').trim()
  }
}

function formatAnswerValue(value, depth = 0, seen = []) {
  const raw = unwrapAnswerJson(value)
  if (!hasAnswerValue(raw)) return ''
  if (depth >= MAX_ANSWER_DEPTH) return '…'
  if (typeof raw !== 'object') return String(raw)
  if (seen.includes(raw)) return '[循环引用]'

  const nextSeen = seen.concat([raw])
  if (Array.isArray(raw)) {
    return raw.map(item => formatAnswerValue(item, depth + 1, nextSeen)).filter(Boolean).join(' / ')
  }

  return Object.keys(raw).sort()
    .map(key => `${key}：${formatAnswerValue(raw[key], depth + 1, nextSeen) || '-'}`)
    .join('，')
}

function resolveAnswerOption(value, options) {
  const raw = unwrapAnswerJson(value)
  if (!hasAnswerValue(raw) || (typeof raw === 'object' && !Array.isArray(raw))) return ''
  const text = String(raw)
  const option = options.map(answerOption).find(item => item.key === text || item.content === text)
  if (!option) return text
  return option.content && option.content !== option.key ? `${option.key}. ${option.content}` : option.key
}

function formatMatchingAnswer(value, question = null) {
  const pair = unwrapAnswerJson(value)
  if (!isObject(pair)) return formatAnswerValue(pair)
  const left = answerField(pair, ['name', 'left', 'source', 'from'])
  const right = answerField(pair, ['content', 'right', 'target', 'to'])
  if (left !== undefined || right !== undefined) {
    const leftText = resolveAnswerOption(left, matchingLeftOptions(question || {})) || formatAnswerValue(left) || '-'
    const rightText = resolveAnswerOption(right, matchingRightOptions(question || {})) || formatAnswerValue(right) || '-'
    return `${leftText} -> ${rightText}`
  }
  return formatAnswerValue(pair)
}

function clockGroupDisplayName(group, index, total) {
  if (total <= 1) return ''

  const value = answerField(group, ['name', 'key', 'type'])
  const name = value === undefined || value === null ? '' : String(value).trim()
  return !name || /^\d+$/.test(name) ? `第${index + 1}组` : name
}

function formatClockAnswer(value) {
  const groups = Array.isArray(value) ? value : [value]
  return groups.map((rawGroup, index) => {
    const group = unwrapAnswerJson(rawGroup)
    if (!isObject(group)) return formatAnswerValue(group)
    const times = Array.isArray(unwrapAnswerJson(group.data)) ? unwrapAnswerJson(group.data) : [group]
    const values = times.map((rawTime) => {
      const time = unwrapAnswerJson(rawTime)
      if (!isObject(time)) return formatAnswerValue(time)
      const period = answerField(time, ['period'])
      const hour = answerField(time, ['hour'])
      const minute = answerField(time, ['minute'])
      if (hour === undefined && minute === undefined) return formatAnswerValue(time)
      return `${period ? `${period} ` : ''}${String(hour ?? '00').padStart(2, '0')}:${String(minute ?? '00').padStart(2, '0')}`
    }).filter(Boolean)
    const answerText = values.join('、') || '-'
    const groupName = clockGroupDisplayName(group, index, groups.length)
    return groupName ? `${groupName}：${answerText}` : answerText
  }).filter(Boolean).join('；')
}

function formatOptionValues(value, options) {
  const raw = unwrapAnswerJson(value)
  const normalized = isObject(raw) ? indexedAnswerValue(raw) : raw
  const values = Array.isArray(normalized)
    ? normalized
    : String(normalized ?? '').split(/\s*[,，;；]\s*/).filter(Boolean)
  return values
    .map(item => resolveAnswerOption(item, options) || formatAnswerValue(item))
    .filter(Boolean)
    .join('、')
}

function formatAssessmentAnswer(value, options) {
  const raw = unwrapAnswerJson(value)
  const answerFields = ['content', 'answer', 'value', 'selected']
  const directFields = ['name', ...answerFields]
  const isIndexedMap = isObject(raw) && !directFields.some(field => Object.prototype.hasOwnProperty.call(raw, field))
  const hasNamedRows = Array.isArray(raw) && raw.some(item => isObject(item) &&
    Object.prototype.hasOwnProperty.call(item, 'name') &&
    answerFields.some(field => Object.prototype.hasOwnProperty.call(item, field)))

  if (isIndexedMap || hasNamedRows) return formatIndexedOptionAnswers(value, () => options)

  const source = Array.isArray(raw) ? raw : [raw]
  return source
    .map(item => resolveAnswerOption(assessmentAnswerValue(item), options) || formatAnswerValue(assessmentAnswerValue(item)))
    .filter(Boolean)
    .join('、')
}

function formatIndexedOptionAnswers(value, optionResolver) {
  const values = namedAnswerValues(value)
  return values.map((rawItem, index) => {
    const item = unwrapAnswerJson(rawItem)
    if (!isObject(item)) return formatAnswerValue(item)
    const name = answerField(item, ['name', 'key', 'id', 'question_id']) ?? index + 1
    const content = indexedAnswerValue(item)
    const formatted = formatOptionValues(content, optionResolver(name, index))
    return `第${name}小题：${formatted || '-'}`
  }).filter(Boolean).join('；')
}

export function normalizeQuestionAnswerValues(answer) {
  const value = unwrapAnswerJson(answer)
  if (!hasAnswerValue(value)) return []
  return (Array.isArray(value) ? value : [value])
    .map(item => unwrapAnswerJson(item))
    .filter(item => item !== null && typeof item !== 'undefined' && typeof item !== 'object')
    .map(item => String(item))
}

export function formatQuestionAnswer(answer, emptyText = '未作答', question = null) {
  const value = unwrapAnswerJson(answer)
  if (isQuestionAnswerEmpty(value, question || {})) return emptyText

  const type = Number(question && (question.question_type || question.questionType))
  const options = answerOptions(question)
  if (type === 1 || type === 2) {
    const values = Array.isArray(value) ? value : [value]
    return values.map(item => resolveAnswerOption(item, options) || formatAnswerValue(item)).filter(Boolean).join('、') || emptyText
  }
  if (type === 3) {
    const values = Array.isArray(value) ? value : [value]
    return values.map((item, index) => `空${index + 1}：${formatAnswerValue(item) || '-'}`).join('；')
  }
  if (type === 4) {
    const normalized = String(value).trim().toLowerCase()
    if (value === true || ['true', '1', 't', 'yes', 'right', 'correct', '正确', '对', '是'].includes(normalized)) return '正确'
    if (value === false || ['false', '0', 'f', 'no', 'wrong', 'incorrect', '错误', '错', '否'].includes(normalized)) return '错误'
  }
  if (type === 5) {
    const values = Array.isArray(value) ? value : [value]
    return values.map(item => formatAnswerValue(item)).filter(Boolean).join('；') || emptyText
  }
  if (type === 6) {
    const values = Array.isArray(value) ? value : [value]
    return values.map(item => formatMatchingAnswer(item, question)).filter(Boolean).join('；') || emptyText
  }
  if (type === 7) {
    const sharedOptions = answerOptions(question)
    return formatIndexedOptionAnswers(value, () => sharedOptions) || emptyText
  }
  if (type === 8) return formatAssessmentAnswer(value, assessmentOptions(question || {})) || emptyText
  if (type === 9) return formatClockAnswer(value) || emptyText
  if (type === 10) {
    return namedAnswerValues(value).map((rawItem, index) => {
      const item = unwrapAnswerJson(rawItem)
      const name = answerField(item, ['name', 'key', 'index']) || index + 1
      const selected = isObject(item)
        ? indexedAnswerValue(item)
        : (Array.isArray(item) ? item[0] : item)
      return `空${name}：${resolveAnswerOption(selected, options) || formatAnswerValue(selected) || '-'}`
    }).join('；')
  }
  if (type === 11) {
    const subQuestions = optionalQuestions(question || {})
    return formatIndexedOptionAnswers(value, (name, index) => {
      const subQuestion = subQuestions.find(item => String(item.answer_name) === String(name)) || subQuestions[index]
      return subQuestion ? subQuestion.options : []
    }) || emptyText
  }
  if (!type && Array.isArray(value)) {
    const values = value.map(item => formatQuestionAnswer(item, '', {})).filter(Boolean)
    return values.length ? values.join('；') : emptyText
  }
  if (!type && isObject(value)) return formatMatchingAnswer(value) || emptyText

  return formatAnswerValue(value) || emptyText
}
